// @flow
import type { AjaxExceptionError, AxiosExceptionError, ErrorMessage } from '../types'

export function getProcessedErrorMessage(error: AxiosExceptionError | AjaxExceptionError) {
    if (error.response) {

        let status = 0;
        let responseData;

        // get properties from each kind of request error
        if(error.isAxiosError) {
            const axiosError = (error: AxiosExceptionError);
            responseData = axiosError.response?.data;
            status = axiosError.response?.status || status;
        } else if(error.name === "AjaxError") {
            const ajaxError = (error: AjaxExceptionError);
            responseData = ajaxError.response;
            status = ajaxError.status || status;
        }

        // check received error message
        let receivedErrorMessage = responseData?.err;
        const isNot500ErrorLoggedInSplunk = responseData?.error && !receivedErrorMessage && !responseData?.transaction_id;
        const isNot400PageNotFound = status !== 404;
        if (isNot500ErrorLoggedInSplunk && isNot400PageNotFound) {
            receivedErrorMessage = responseData?.error;
        }

        if (receivedErrorMessage) {
            return receivedErrorMessage;
        }

        // Handle 5xx errors
        if (status >= 500) {
            switch (status) {
                case 500: // Internal Server Error
                    return 'The server encountered an error processing the request. Try again later.';
                case 501: // Not Implemented
                    return 'The server does not know how to respond to that request.';
                case 502: // Bad Gateway
                case 503: // Service Unavailable
                case 504: // Gateway Timeout
                    return 'The server could not be reached or is not responding. Try again later.';
                default:
                    return 'Something went wrong while talking with the server. Try again later.';
            }
        }

        if (status >= 400) {
            switch (status) {
                case 401: // Unauthorized (not authenticated)
                    return 'You must be logged in to do that action.';
                case 403: // Forbidden (not authorized)
                    return 'Your permissions do not permit you to do that action.';
                case 404: // Not Found
                    return 'No data for that resource was found.';
                case 405: // Validation Error
                    if (responseData?.errors?.length) {
                        return responseData.errors.sort().join('\n');
                    }
                    return 'There are errors in your request. Please check your inputs and try again.';
                case 422: // Unprocessable Entity
                    return 'There are errors in your request, check your inputs and try again.';
                default:
                    return 'A problem with your request has occurred. Try again later.';
            }
        }

        return 'A temporary problem has caused your request to fail. Try again later.';
    }

    // Error with the request (no server response)
    if (error.request) {
        return 'Something went wrong while talking to the server';
    }

    // Raw Axios/Ajax or other error describing the problem (technical) || Unknown failure
    return error.message || 'Something went wrong unexpectedly';
}

export function parseFormattedErrorMessageString(errorMsg?: string): ?ErrorMessage {
    if(!errorMsg) return null;

    if(!errorMsg.includes('|')) return {
        type: 'generic_error',
        error: errorMsg
    };

    const [errorType, ...errors] = errorMsg.split('|');

    // 'rules_failed|You have a prior reservation that is overlapping the current reservation'
    if(errorType === 'rules_failed') {
        return {
            type: 'rules_failed',
            error: errors[0]
        };
    }

    // 'validations_failed|issue_station:Issue Station is require'
    if(errorType === 'validations_failed') {
        return {
            type: 'validations_failed',
            errors: errors.map( (e) => {
                const [propPath, error] = e?.split(':')
                return { propPath, error}
            } )
        };
    }

    return {
        type: 'generic_error',
        error: errors.join('')
    };
}

export function parseFormattedErrorMessageStringIntoMapOfErrors(formattedErrorMessage?: string) {
    if(!formattedErrorMessage) return {}

    const [, ...errors] = formattedErrorMessage.split('|')

    if(!errors?.length) return {};

    return errors.reduce((res, e) => {
        const [propPath, errorMessage] = e.split(':')
        res[propPath] = errorMessage;
        return res;
    }, {})
}

export function parseErrorMessageIntoStringList(errorMessage: ?ErrorMessage): ?string[] {
    if(!errorMessage) return null

    if(errorMessage.type === 'generic_error') return [errorMessage.error];

    if(errorMessage.type === 'rules_failed') return [errorMessage.error];

    if(errorMessage.type === 'validations_failed') {
        return errorMessage.errors.map(({error}) => error)
    }

    return null;
}
